/**
 * Privacy-safe, provider-agnostic funnel analytics for the apply form.
 *
 * This is a thin emitter: events are dispatched to any registered sink, and it
 * stays a no-op until one is. The counters sit behind `web-analytics-sink.ts`,
 * registered once from `main.tsx`; keeping them behind a sink is what lets this
 * module — and the form — stay unaware of which providers are in play.
 *
 * STRICT PII RULE (one-common-repo decision
 * "Согласие и retention данных PromoLanding"): events MUST NOT carry phone,
 * email, BIN, the free-text description, or any form payload. Only funnel stage
 * and coarse categorical attributes (e.g. which field category failed) are
 * allowed. The `ApplyAnalyticsProps` type below is intentionally narrow to make
 * leaking a value a type error, not a review catch.
 */

export const applyAnalyticsEvents = {
  formOpened: 'promo_apply_form_opened',
  fillStarted: 'promo_apply_fill_started',
  submitAttempted: 'promo_apply_submit_attempted',
  submitSucceeded: 'promo_apply_submit_succeeded',
  validationFailed: 'promo_apply_validation_failed',
  submitFailed: 'promo_apply_submit_failed',
} as const;

export type ApplyAnalyticsEvent = (typeof applyAnalyticsEvents)[keyof typeof applyAnalyticsEvents];

/**
 * Allowed event attributes. Deliberately excludes any free-form/value carrier.
 * `fields` holds only field *categories* (form field names), never values.
 */
export interface ApplyAnalyticsProps {
  /** Field categories that failed validation — names only, never values. */
  readonly fields?: ReadonlyArray<string>;
  /** Coarse technical error category for submit failures (e.g. an HTTP class). */
  readonly errorCategory?: string;
}

export type ApplyAnalyticsSink = (event: ApplyAnalyticsEvent, props?: ApplyAnalyticsProps) => void;

const sinks = new Set<ApplyAnalyticsSink>();

/** Register a provider sink. Returns an unsubscribe function. */
export function registerApplyAnalyticsSink(sink: ApplyAnalyticsSink): () => void {
  sinks.add(sink);
  return () => sinks.delete(sink);
}

/** Remove every registered sink (used by tests and teardown). */
export function resetApplyAnalyticsSinks(): void {
  sinks.clear();
}

/** Emit a funnel event to all sinks. Never throws into the caller. */
export function emitApplyEvent(event: ApplyAnalyticsEvent, props?: ApplyAnalyticsProps): void {
  for (const sink of sinks) {
    try {
      sink(event, props);
    } catch {
      // A misbehaving analytics sink must never break the form.
    }
  }
}
